"use client";

import * as React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import type { InputProps } from "@/components/ui/input";
import { InputGroup, InputGroupAddon, InputGroupInput, InputGroupText } from "@/components/ui/input-group";
import { NAMESPACES } from "@/i18n/constants";
import { ICON_KEYS, iconRegistry } from "@/icons";

const SearchIcon = iconRegistry[ICON_KEYS.SEARCH];
const ClearIcon = iconRegistry[ICON_KEYS.CLOSE];

export type SearchInputProps = Omit<InputProps, "type" | "value" | "onChange" | "ref"> & {
    value: string;
    onValueChange: (value: string) => void;
    /** Styles the surrounding input group; the field fills it and takes the group's layout. */
    className?: string;
    /** Adds a trailing clear button, held in place (invisible) while the field is empty. */
    clearable?: boolean;
    /** Muted text at the end of the field, before the clear button, such as a match count. */
    endText?: string;
};

/** The app's search field: a search-typed input framed by the group, with a leading magnifier. */
export function SearchInput({
    value,
    onValueChange,
    className,
    clearable = false,
    endText,
    ...props
}: SearchInputProps) {
    const { t } = useTranslation(NAMESPACES.COMMON);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const empty = value.length === 0;

    return (
        <InputGroup className={className}>
            <InputGroupAddon>
                <SearchIcon aria-hidden={"true"} />
            </InputGroupAddon>
            <InputGroupInput
                ref={inputRef}
                type={"search"}
                aria-label={t("actions.search")}
                value={value}
                onChange={(event) => onValueChange(event.target.value)}
                {...props}
            />
            {(clearable || endText) && (
                <InputGroupAddon align={"inline-end"}>
                    {endText && (
                        <InputGroupText aria-live={"polite"} className={"whitespace-nowrap text-xs tabular-nums"}>
                            {endText}
                        </InputGroupText>
                    )}
                    {clearable && (
                        <Button
                            aria-hidden={empty}
                            aria-label={t("actions.clear-search")}
                            className={empty ? "invisible" : undefined}
                            disabled={empty}
                            onClick={() => {
                                onValueChange("");
                                inputRef.current?.focus();
                            }}
                            size={"icon"}
                            variant={"ghost"}
                        >
                            <ClearIcon aria-hidden={"true"} />
                        </Button>
                    )}
                </InputGroupAddon>
            )}
        </InputGroup>
    );
}
